import { useQuery } from '@tanstack/react-query';
import { useDataProvider } from 'react-admin';
import { useRegion } from '../auth/RegionContext';
import {
  buildStorePerformanceView,
  type StorePerfPeriod,
  type StorePerfView,
} from './storePerformanceModel';
import type { Click, Merchant, MerchantRegion } from '../dataProvider/types';

/**
 * Region-driven Store performance view, read through the aggregation seam
 * (`buildStorePerformanceView`), NOT computed in the component. For v1 it pulls
 * the raw clicks, merchants and merchant regions from the mock provider and
 * aggregates client-side; the real-data migration replaces the fetch-and-aggregate
 * here with a single server-side endpoint and the surface does not change. Same
 * seam idiom as useEarnings.
 *
 * Scope comes from the region context (UK | US | GLOBAL): the surface shows every
 * band the scope derives to at once, so it keys off `region`, not `activeRegion`.
 * `now` is injected so the period window and the maturation cutoff are
 * deterministic under test.
 */

const ALL = {
  pagination: { page: 1, perPage: 100_000 },
  sort: { field: 'id', order: 'ASC' as const },
  filter: {},
};

export const useStorePerformance = (opts: {
  period: StorePerfPeriod;
  now: number;
}): { view: StorePerfView | null; isLoading: boolean } => {
  const dataProvider = useDataProvider();
  const { region } = useRegion();

  const { data, isLoading } = useQuery({
    queryKey: ['storePerformance', region, opts.period, opts.now],
    queryFn: async () => {
      const [clicks, merchants, merchantRegions] = await Promise.all([
        dataProvider.getList('clicks', ALL),
        dataProvider.getList('merchants', ALL),
        dataProvider.getList('merchantRegions', ALL),
      ]);
      return buildStorePerformanceView(
        {
          clicks: clicks.data as unknown as Click[],
          merchants: merchants.data as unknown as Merchant[],
          merchantRegions: merchantRegions.data as unknown as MerchantRegion[],
        },
        { scope: region, period: opts.period, now: opts.now },
      );
    },
  });

  return { view: data ?? null, isLoading };
};
